import { Blackboard } from '../Blackboard';
import { TaskStatus } from '../TaskStatus';
/**
 * 黑板比较操作符
 */
export var CompareOperator;
(function (CompareOperator) {
    CompareOperator["Equal"] = "equal";
    CompareOperator["NotEqual"] = "notEqual";
    CompareOperator["Greater"] = "greater";
    CompareOperator["GreaterOrEqual"] = "greaterOrEqual";
    CompareOperator["Less"] = "less";
    CompareOperator["LessOrEqual"] = "lessOrEqual";
    CompareOperator["Contains"] = "contains";
    CompareOperator["NotContains"] = "notContains";
})(CompareOperator || (CompareOperator = {}));
/**
 * 黑板值比较条件
 *
 * @description 比较黑板变量与指定值或另一个黑板变量
 *
 * @example
 * ```typescript
 * // 检查玩家生命值是否大于50
 * const healthCheck = new BlackboardValueComparison<GameContext>(
 *   'playerHealth',
 *   CompareOperator.Greater,
 *   50
 * );
 *
 * // 比较两个黑板变量
 * const compareVars = new BlackboardValueComparison<GameContext>(
 *   'playerHealth',
 *   CompareOperator.Greater,
 *   null,
 *   'enemyHealth'
 * );
 * ```
 */
export class BlackboardValueComparison {
    constructor(variableName, operator, compareValue = null, compareVariable) {
        this.discriminator = 'IConditional';
        this.variableName = variableName;
        this.operator = operator;
        this.compareValue = compareValue;
        this.compareVariable = compareVariable;
    }
    /**
     * 检查条件是否满足
     */
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('BlackboardValueComparison: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        if (!blackboard.hasVariable(this.variableName)) {
            console.warn(`BlackboardValueComparison: 变量 "${this.variableName}" 不存在`);
            return TaskStatus.Failure;
        }
        const leftValue = blackboard.getValue(this.variableName);
        let rightValue;
        if (this.compareVariable) {
            if (!blackboard.hasVariable(this.compareVariable)) {
                console.warn(`BlackboardValueComparison: 比较变量 "${this.compareVariable}" 不存在`);
                return TaskStatus.Failure;
            }
            rightValue = blackboard.getValue(this.compareVariable);
        }
        else {
            rightValue = this.compareValue;
        }
        const result = this._performComparison(leftValue, rightValue, this.operator);
        return result ? TaskStatus.Success : TaskStatus.Failure;
    }
    /**
     * 执行比较操作
     */
    _performComparison(left, right, operator) {
        switch (operator) {
            case CompareOperator.Equal:
                return left === right;
            case CompareOperator.NotEqual:
                return left !== right;
            case CompareOperator.Greater:
                return typeof left === 'number' && typeof right === 'number' && left > right;
            case CompareOperator.GreaterOrEqual:
                return typeof left === 'number' && typeof right === 'number' && left >= right;
            case CompareOperator.Less:
                return typeof left === 'number' && typeof right === 'number' && left < right;
            case CompareOperator.LessOrEqual:
                return typeof left === 'number' && typeof right === 'number' && left <= right;
            case CompareOperator.Contains:
                if (typeof left === 'string' && typeof right === 'string') {
                    return left.includes(right);
                }
                if (Array.isArray(left)) {
                    return left.includes(right);
                }
                return false;
            case CompareOperator.NotContains:
                if (typeof left === 'string' && typeof right === 'string') {
                    return !left.includes(right);
                }
                if (Array.isArray(left)) {
                    return !left.includes(right);
                }
                return true;
            default:
                return false;
        }
    }
}
/**
 * 黑板变量存在性检查
 *
 * @description 检查指定的黑板变量是否存在且不为null/undefined
 */
export class BlackboardVariableExists {
    constructor(variableName, invert = false) {
        this.discriminator = 'IConditional';
        this.variableName = variableName;
        this.invert = invert;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('BlackboardVariableExists: 上下文中未找到Blackboard实例');
            return this.invert ? TaskStatus.Success : TaskStatus.Failure;
        }
        const exists = blackboard.hasVariable(this.variableName);
        const value = exists ? blackboard.getValue(this.variableName) : undefined;
        const isValid = exists && value !== null && value !== undefined;
        const result = this.invert ? !isValid : isValid;
        return result ? TaskStatus.Success : TaskStatus.Failure;
    }
}
/**
 * 黑板变量类型检查
 *
 * @description 检查黑板变量是否为指定类型
 */
export class BlackboardVariableTypeCheck {
    constructor(variableName, expectedType) {
        this.discriminator = 'IConditional';
        this.variableName = variableName;
        this.expectedType = expectedType;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('BlackboardVariableTypeCheck: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        const variableDefinition = blackboard.getVariableDefinition(this.variableName);
        if (!variableDefinition) {
            return TaskStatus.Failure;
        }
        const result = variableDefinition.type === this.expectedType;
        return result ? TaskStatus.Success : TaskStatus.Failure;
    }
}
/**
 * 黑板变量范围检查
 *
 * @description 检查数值型黑板变量是否在指定范围内
 */
export class BlackboardVariableRangeCheck {
    constructor(variableName, minValue, maxValue) {
        this.discriminator = 'IConditional';
        this.variableName = variableName;
        this.minValue = minValue;
        this.maxValue = maxValue;
    }
    update(context) {
        const blackboard = context.blackboard;
        if (!blackboard || !(blackboard instanceof Blackboard)) {
            console.warn('BlackboardVariableRangeCheck: 上下文中未找到Blackboard实例');
            return TaskStatus.Failure;
        }
        if (!blackboard.hasVariable(this.variableName)) {
            return TaskStatus.Failure;
        }
        const value = blackboard.getValue(this.variableName);
        if (typeof value !== 'number') {
            return TaskStatus.Failure;
        }
        const result = value >= this.minValue && value <= this.maxValue;
        return result ? TaskStatus.Success : TaskStatus.Failure;
    }
}
//# sourceMappingURL=BlackboardConditionals.js.map